import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { collection, doc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import InputGroup from 'react-bootstrap/InputGroup';
import Alert from 'react-bootstrap/Alert';
import { db, functions, auth } from '../firebase.js';
import { useAuth } from '../contexts/AuthContext.js';

const ADMIN_EMAIL = 'ccrane2@alumni.nd.edu';

export default function Account() {
  const { currentUser, authLoaded } = useAuth();
  const [players, setPlayers] = useState([]);
  const [linkedPlayer, setLinkedPlayer] = useState(undefined); // undefined = loading
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [inviteLink, setInviteLink] = useState(null);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [copied, setCopied] = useState(false);

  const [recalculating, setRecalculating] = useState(false);
  const [recalculateError, setRecalculateError] = useState(null);
  const [recalculateResult, setRecalculateResult] = useState(null);

  useEffect(() => {
    if (!currentUser) return;

    getDocs(collection(db, 'players')).then(snap => {
      setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    getDocs(query(collection(db, 'players'), where('uid', '==', currentUser.uid))).then(snap => {
      setLinkedPlayer(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
    });
  }, [currentUser]);

  async function handleLink(e) {
    e.preventDefault();
    if (!selectedId) return;
    setSaving(true);
    setError(null);
    try {
      await setDoc(doc(db, 'players', selectedId), { uid: currentUser.uid }, { merge: true });
      setLinkedPlayer(players.find(p => p.id === selectedId) ?? null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleGenerateInvite() {
    setGeneratingInvite(true);
    setInviteError(null);
    try {
      const generateAccessCode = httpsCallable(functions, 'generateAccessCode');
      const result = await generateAccessCode();
      setInviteLink(`${window.location.origin}/login?code=${result.data.code}`);
      setCopied(false);
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setGeneratingInvite(false);
    }
  }

  async function handleCopyInvite() {
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
  }

  async function handleRecalculateStats() {
    setRecalculating(true);
    setRecalculateError(null);
    setRecalculateResult(null);
    try {
      const recalculateStats = httpsCallable(functions, 'recalculateStats');
      const result = await recalculateStats();
      setRecalculateResult(result.data);
    } catch (err) {
      setRecalculateError(err.message);
    } finally {
      setRecalculating(false);
    }
  }

  if (authLoaded && !currentUser) return <Navigate to="/login" replace />;
  if (linkedPlayer === undefined) return <p>Loading…</p>;

  return (
    <div className="center-page">
      <div className="w-100">
        <div className="panel mb-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '0.5rem' }}>
            <h2 className="mb-0">Account</h2>
            <Button variant="primary" size="sm" onClick={() => signOut(auth)}>Sign Out</Button>
          </div>
          <p>{currentUser.email}</p>

          {linkedPlayer ? (
            <p>
              Linked to player: <Link to={`/players/${linkedPlayer.id}`}>{linkedPlayer.name}</Link>
            </p>
          ) : (
            <Form onSubmit={handleLink}>
              {error && <Alert variant="danger">{error}</Alert>}
              <Form.Group className="mb-3">
                <Form.Label>Link your login to an existing player</Form.Label>
                <Form.Select value={selectedId} onChange={e => setSelectedId(e.target.value)}>
                  <option value="">Select a player…</option>
                  {players.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Button type="submit" disabled={saving || !selectedId}>Link account</Button>
            </Form>
          )}

          {currentUser.email === ADMIN_EMAIL && (
            <div className="mt-4 pt-3 border-top">
              <p>
                <Link to="/dev">Dev tools →</Link>
              </p>

              <h5>Invite a new player</h5>
              {inviteError && <Alert variant="danger">{inviteError}</Alert>}
              <Button onClick={handleGenerateInvite} disabled={generatingInvite}>
                {generatingInvite ? 'Generating…' : 'Generate invite link'}
              </Button>
              {inviteLink && (
                <Form.Group className="mt-3">
                  <Form.Label>Share this link — it lets them create an account:</Form.Label>
                  <InputGroup>
                    <Form.Control readOnly value={inviteLink} onFocus={e => e.target.select()} />
                    <Button onClick={handleCopyInvite}>
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                  </InputGroup>
                </Form.Group>
              )}

              <div className="mt-4 pt-3 border-top">
                <h5>Recalculate stats</h5>
                <p className="text-muted">
                  Rebuilds every player's wins/losses/ties/points and the homepage standings from the full games
                  history, same as what happens after a game is saved.
                </p>
                {recalculateError && <Alert variant="danger">{recalculateError}</Alert>}
                {recalculateResult && (
                  <Alert variant="success">
                    Updated {recalculateResult.playersUpdated} player(s) from {recalculateResult.gamesScanned} game(s).
                  </Alert>
                )}
                <Button onClick={handleRecalculateStats} disabled={recalculating}>
                  {recalculating ? 'Recalculating…' : 'Recalculate stats'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
